import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import ConnectSlackTooltip from "@/organization/slack/ConnectSlackTooltip"
import { SlackChannelSelectQuery } from "@/organization/slack/__generated__/SlackChannelSelectQuery.graphql"
import Relay from "@/relay/relayUtils"
import { DiscoIcon, DiscoInputSkeleton, DiscoSelect, DiscoSelectProps } from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { useTheme } from "@material-ui/core"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, useLazyLoadQuery } from "react-relay"

type Props = TestIDProps &
  Pick<
    DiscoSelectProps<string, string>,
    "value" | "onChange" | "disableClearable" | "className"
  > & {
    placeholder?: string
  }

type SlackChannelSelectOption = {
  value: string
  title: string
  isPrivate: boolean
}

function SlackChannelSelect(props: Props) {
  const { testid = "SlackChannelSelect", ...rest } = props

  const theme = useTheme()

  const activeOrganization = useActiveOrganization()!
  const { organization } = useLazyLoadQuery<SlackChannelSelectQuery>(
    graphql`
      query SlackChannelSelectQuery($id: ID!) {
        organization: node(id: $id) {
          ... on Organization {
            slack {
              availableChannels {
                id
                name
                type
              }
            }
            ...ConnectSlackTooltipFragment @relay(mask: false)
            ...ConnectSlackTooltipFragment
          }
        }
      }
    `,
    { id: activeOrganization.id },
    {
      fetchPolicy: "network-only",
    }
  )

  const channels = organization?.slack?.availableChannels ?? []

  if (!organization) return null

  return (
    <ConnectSlackTooltip organizationKey={organization}>
      <DiscoSelect
        testid={`${testid}.select`}
        placeholder={"Select a channel"}
        autoComplete={false}
        {...rest}
        options={channels.map((channel) => ({
          value: channel.id,
          title: channel.name,
          isPrivate: channel.type === "private",
        }))}
        renderOption={renderOption}
        renderValue={renderValue}
        filterOptions={{ maxVisible: 100 }}
        disabled={
          !organization.viewerMembership?.organizationMembershipSlackConnection?.id
        }
      />
    </ConnectSlackTooltip>
  )

  function renderOption(option: SlackChannelSelectOption) {
    return (
      <DiscoDropdownItem
        testid={`${testid}.option.add.${option.title}`}
        title={`#${option.title}`}
        icon={option.isPrivate ? <DiscoIcon icon={"lock"} /> : undefined}
        iconSize={16}
      />
    )
  }

  function renderValue(option: SlackChannelSelectOption) {
    return (
      <DiscoTag
        key={option.value}
        testid={`${testid}.option.remove.${option.title}`}
        name={`#${option.title}`}
        leftIcon={
          option.isPrivate ? (
            <DiscoIcon
              icon={"lock"}
              height={16}
              width={16}
              color={theme.palette.text.primary}
            />
          ) : undefined
        }
      />
    )
  }
}

function SlackChannelSelectSkeleton() {
  return <DiscoInputSkeleton />
}

export default Relay.withSkeleton({
  component: SlackChannelSelect,
  skeleton: SlackChannelSelectSkeleton,
})
